import { computed } from "vue";
import { isAcceptedFileType, normalizeAccept } from "../utils/fileUploadAccept";
import type { Ref } from "vue";
import type { DSFileUploadComponentProps, DSFileUploadModelValue, DSFileUploadRejectedFile } from "../types";

interface UseDSFileUploadValidationOptions {
  props: DSFileUploadComponentProps;
  modelValue: Readonly<Ref<DSFileUploadModelValue>>;
}

export function useDSFileUploadValidation({ props, modelValue }: UseDSFileUploadValidationOptions) {
  const acceptedTypes = computed(() => normalizeAccept(props.accept));
  const acceptAttribute = computed(() => acceptedTypes.value.join(","));

  function validateSelectedFiles(files: File[]): { accepted: File[]; rejected: DSFileUploadRejectedFile[] } {
    const acceptedFiles: File[] = [];
    const rejectedFiles: DSFileUploadRejectedFile[] = [];

    files.forEach(file => {
      const rejectedFile = buildRejectedFile(file, acceptedFiles.length);
      if (rejectedFile) {
        rejectedFiles.push(rejectedFile);
        return;
      }

      acceptedFiles.push(file);
    });

    return { accepted: acceptedFiles, rejected: rejectedFiles };
  }

  function buildRejectedFile(file: File, acceptedCount: number): DSFileUploadRejectedFile | undefined {
    if (isMaxFilesExceeded(acceptedCount)) return { file, reason: "maxFiles", message: maxFilesMessage() };
    if (!isAcceptedFileType(file, acceptedTypes.value)) {
      return { file, reason: "type", message: unsupportedFileTypeMessage(file) };
    }

    if (isTooLarge(file)) return { file, reason: "size", message: fileTooLargeMessage(file) };
  }

  function isMaxFilesExceeded(acceptedCount: number): boolean {
    if (!props.multiple) return acceptedCount >= 1;

    return props.maxFiles !== undefined && modelValue.value.length + acceptedCount >= props.maxFiles;
  }

  function maxFilesMessage(): string {
    return window.I18n.designSystem.fileUpload.maxFiles.replace(
      "%{max_files}",
      String(props.multiple ? props.maxFiles : 1)
    );
  }

  function unsupportedFileTypeMessage(file: File): string {
    return window.I18n.designSystem.fileUpload.unsupportedFileType.replace("%{file_name}", () => file.name);
  }

  function fileTooLargeMessage(file: File): string {
    return window.I18n.designSystem.fileUpload.fileTooLarge.replace("%{file_name}", () => file.name);
  }

  function isTooLarge(file: File): boolean {
    return props.maxFileSize !== undefined && file.size > props.maxFileSize;
  }

  return {
    acceptAttribute,
    validateSelectedFiles,
  };
}
